import { useState } from "react";
import {
  ArrowRight,
  Calendar,
  ChevronDown,
  ListChecks,
  Lock,
  Mail,
  MapPin,
  Phone,
  UtensilsCrossed,
} from "lucide-react";
import clsx from "clsx";
import type { ActionItem, Activity } from "@/api/types";
import type { Side } from "@/lib/sides";
import { ACTIVITY_ROLES, roleVar, stageColor } from "@/lib/colors";
import { eur, fmtDay } from "@/lib/format";
import { Avatar } from "@/components/ui";
import ActionItems from "@/components/ActionItems";

const norm = (v: string | null | undefined) => (v ?? "").trim().toLowerCase();

const TYPE_ICON = {
  call: Phone,
  email: Mail,
  meeting: Calendar,
  note: Lock,
  hospitality: UtensilsCrossed,
  status_change: ArrowRight,
} as const;

const TYPE_LABEL: Record<Activity["type"], string> = {
  call: "Call",
  email: "Email",
  meeting: "Meeting",
  note: "Internal note",
  hospitality: "Hospitality",
  status_change: "Stage change",
};

function splitText(a: Activity): { title: string | null; body: string | null } {
  const title = a.detail?.title ?? null;
  const text = a.text ?? "";
  if (title) return { title, body: a.detail?.body || text || null };
  // Synced emails store "subject\nsnippet" in text.
  if (a.source === "gmail") {
    const [first, ...rest] = text.split("\n");
    return { title: first || null, body: a.detail?.body || rest.join("\n") || null };
  }
  return { title: null, body: a.detail?.body || text || null };
}

/** "14:32" in the viewer's time zone, when the exact time is known. */
function timeLabel(iso: string | null | undefined): string | null {
  if (!iso) return null;
  return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function withWhom(a: Activity): string[] {
  const author = norm(a.author);
  return (a.detail?.participants ?? [])
    .map((p) => p.name || p.email)
    .filter((n) => n && norm(n) !== author);
}

/** One interaction as a chat bubble. */
export function Bubble({
  activity: a,
  side,
  items,
  canEdit,
  currentUserId,
  onItemsMutated,
}: {
  activity: Activity;
  side: Exclude<Side, "both">;
  items: ActionItem[];
  canEdit: boolean;
  currentUserId: string;
  onItemsMutated: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [tasksOpen, setTasksOpen] = useState(items.some((i) => i.status === "open"));
  const role = ACTIVITY_ROLES[a.type] ?? "azure";
  const Icon = TYPE_ICON[a.type];
  const { title, body } = splitText(a);
  const long = (body ?? "").length > 280;
  const shown = expanded || !long ? body : `${(body ?? "").slice(0, 280).trimEnd()}…`;
  const ours = side === "us";
  const note = a.type === "note";
  const others = withWhom(a);

  return (
    <div className={clsx("group flex items-end gap-2.5", ours && "flex-row-reverse")}>
      <Avatar name={a.author} size={28} muted={!ours} />
      <div
        className={clsx("min-w-0 max-w-[78%] flex flex-col", ours ? "items-end" : "items-start")}
      >
        <div
          className={clsx(
            "mb-1 flex items-center gap-1.5 text-xs text-mut-500",
            ours && "flex-row-reverse",
          )}
        >
          <span className="font-medium text-mut-300">{a.author ?? (ours ? "You" : "Them")}</span>
          <span className="chip-tint !py-0" style={roleVar(role)}>
            <Icon className="h-3 w-3" />
            {TYPE_LABEL[a.type]}
          </span>
          {timeLabel(a.occurred_at) && (
            <span className="tabular-nums">{timeLabel(a.occurred_at)}</span>
          )}
          {a.source && <span>via {a.source === "gmail" ? "Gmail" : "Calendar"}</span>}
        </div>
        <div
          className={clsx(
            "rounded-2xl px-4 py-2.5 text-md leading-6 shadow-[0_1px_2px_rgb(var(--shadow)/0.05)]",
            ours ? "rounded-br-md" : "rounded-bl-md",
            note
              ? "border border-dashed border-accent-cyan/40 bg-accent-cyan/[0.06]"
              : ours
                ? "border border-[rgb(var(--c)/0.25)] bg-[rgb(var(--c)/0.10)]"
                : "border border-fg/[0.08] bg-ink-900",
          )}
          style={roleVar(role)}
        >
          {others.length > 0 && (
            <div className="mb-1 text-xs text-mut-500">with {others.join(", ")}</div>
          )}
          {title && <div className="font-semibold">{title}</div>}
          {shown && <div className="whitespace-pre-wrap break-words">{shown}</div>}
          {a.type === "hospitality" && a.detail?.cost_amount != null && (
            <div className="mt-1 text-sm text-mut-500">{eur(a.detail.cost_amount)}</div>
          )}
          {long && (
            <button
              className="mt-1 text-sm font-medium text-accent-azure"
              onClick={() => setExpanded((e) => !e)}
            >
              {expanded ? "Show less" : "Show all"}
            </button>
          )}
        </div>
        {(items.length > 0 || canEdit) && (
          <div className={clsx("mt-1 w-full max-w-[520px]", ours && "flex flex-col items-end")}>
            <button
              className={clsx(
                "inline-flex items-center gap-1 text-xs text-mut-500 hover:text-mut-100 transition-opacity",
                // Only surface "Add a task" on hover; existing tasks always show.
                items.length === 0 &&
                  !tasksOpen &&
                  "opacity-0 group-hover:opacity-100 focus:opacity-100",
              )}
              onClick={() => setTasksOpen((o) => !o)}
            >
              <ListChecks className="h-3.5 w-3.5" />
              {items.length > 0
                ? `${items.filter((i) => i.status === "done").length}/${items.length} tasks`
                : "Add a task"}
              <ChevronDown
                className={clsx("h-3 w-3 transition-transform", tasksOpen && "rotate-180")}
              />
            </button>
            {tasksOpen && (
              <div className="mt-1 w-full rounded-xl border border-fg/[0.08] bg-ink-900 px-3 py-2 text-left">
                <ActionItems
                  dealId={a.deal_id}
                  parentActivityId={a.id}
                  items={items}
                  canEdit={canEdit}
                  currentUserId={currentUserId}
                  onMutated={onItemsMutated}
                  onAdded={() => setTasksOpen(true)}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/** A meeting or hospitality: a shared moment, shown across the middle. */
export function SharedEvent({ activity: a, today }: { activity: Activity; today: string }) {
  const role = ACTIVITY_ROLES[a.type] ?? "violet";
  const Icon = TYPE_ICON[a.type];
  const { title, body } = splitText(a);
  const people = [a.author, ...withWhom(a)].filter((n): n is string => !!n);
  const upcoming = a.date > today;
  return (
    <div className="mx-auto w-full max-w-[560px]" style={roleVar(role)}>
      <div className="rounded-2xl border border-[rgb(var(--c)/0.25)] bg-[rgb(var(--c)/0.07)] px-4 py-3">
        <div className="flex items-center gap-2 text-xs">
          <span className="chip-tint !py-0">
            <Icon className="h-3 w-3" />
            {TYPE_LABEL[a.type]}
          </span>
          {upcoming && (
            <span className="font-medium text-accent-azure">
              Upcoming · {fmtDay(a.date, "EEE d MMM")}
            </span>
          )}
          {timeLabel(a.occurred_at) && (
            <span className="tabular-nums text-mut-500">{timeLabel(a.occurred_at)}</span>
          )}
          <span className="ml-auto flex -space-x-1.5">
            {people.slice(0, 5).map((p) => (
              <Avatar key={p} name={p} size={22} className="ring-2 ring-ink-900" />
            ))}
          </span>
        </div>
        <div className="mt-1.5 font-semibold text-md">
          {title ?? (body ? body.split("\n")[0] : TYPE_LABEL[a.type])}
        </div>
        {a.detail?.location && (
          <div className="mt-0.5 flex items-center gap-1 text-sm text-mut-500">
            <MapPin className="h-3.5 w-3.5" />
            {a.detail.location}
          </div>
        )}
        {people.length > 0 && <div className="mt-1 text-sm text-mut-500">{people.join(", ")}</div>}
      </div>
    </div>
  );
}

/** "Discovery → Early discussion" as a pill with both stages in colour. */
export function StageChange({ activity: a }: { activity: Activity }) {
  const [from, to] = (a.text ?? "").split("→").map((s) => s.trim());
  return (
    <div className="flex justify-center">
      <span className="inline-flex items-center gap-2 rounded-full border border-fg/[0.08] bg-ink-900 px-3 py-1 text-sm">
        {to ? (
          <>
            <span style={{ color: stageColor(from) }}>{from}</span>
            <ArrowRight className="h-3.5 w-3.5 text-mut-500" />
            <span className="font-semibold" style={{ color: stageColor(to) }}>
              {to}
            </span>
          </>
        ) : (
          <span>{a.text || "Stage changed"}</span>
        )}
        {a.author && <span className="text-xs text-mut-500">· {a.author}</span>}
        {timeLabel(a.occurred_at ?? a.created_at) && (
          <span className="text-xs tabular-nums text-mut-500">
            · {timeLabel(a.occurred_at ?? a.created_at)}
          </span>
        )}
      </span>
    </div>
  );
}

export function DayDivider({ day }: { day: string }) {
  return (
    <div className="flex items-center gap-3 py-1">
      <span className="h-px flex-1 bg-fg/[0.07]" />
      <span className="section-title">{fmtDay(day, "EEE d MMM yyyy")}</span>
      <span className="h-px flex-1 bg-fg/[0.07]" />
    </div>
  );
}

/** A long quiet spell between two interactions, called out so stalls are visible. */
export function Silence({ days }: { days: number }) {
  return (
    <div className="flex justify-center">
      <span className="rounded-full bg-accent-amber/10 px-3 py-1 text-xs font-medium text-accent-amber">
        {days} days without contact
      </span>
    </div>
  );
}
